import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

// Interfaces to match the log structure
interface TTFBLogEntry {
  date_time: string;
  service_name: string;
  ttfb: number;
}

interface EventLogEntry {
  date_time: string;
  event: string;
}

interface NewCallLog {
  ttfb: TTFBLogEntry[];
  events: EventLogEntry[];
}

interface CallLogsProps {
  callLogs: NewCallLog | null;
  loading: boolean;
}

const CallLogs: React.FC<CallLogsProps> = ({ callLogs, loading }) => {
  const [eventSearchTerm, setEventSearchTerm] = useState("");
  const [ttfbSearchTerm, setTtfbSearchTerm] = useState("");
  const [ttfbSortOrder, setTtfbSortOrder] = useState<"asc" | "desc">("desc");
  const [ttfbSortField, setTtfbSortField] = useState<"ttfb" | "timestamp">(
    "timestamp"
  );

  // Filter event logs based on search term
  const filteredEvents = useMemo(() => {
    if (!callLogs?.events) return [];

    return callLogs.events.filter(
      (log) =>
        log.event.toLowerCase().includes(eventSearchTerm.toLowerCase()) ||
        log.date_time.toLowerCase().includes(eventSearchTerm.toLowerCase())
    );
  }, [callLogs?.events, eventSearchTerm]);

  // Filter TTFB entries based on search term
  const filteredTtfb = useMemo(() => {
    if (!callLogs?.ttfb) return [];

    const filtered = callLogs.ttfb.filter(
      (entry) =>
        entry.date_time.toLowerCase().includes(ttfbSearchTerm.toLowerCase()) ||
        entry.service_name.toLowerCase().includes(ttfbSearchTerm.toLowerCase())
    );

    // Sort by selected field
    return [...filtered].sort((a, b) => {
      if (ttfbSortField === "ttfb") {
        const ttfbA = a.ttfb;
        const ttfbB = b.ttfb;
        return ttfbSortOrder === "asc" ? ttfbA - ttfbB : ttfbB - ttfbA;
      } else {
        // Sort by timestamp
        const timeA = new Date(a.date_time).getTime();
        const timeB = new Date(b.date_time).getTime();
        return ttfbSortOrder === "asc" ? timeA - timeB : timeB - timeA;
      }
    });
  }, [callLogs?.ttfb, ttfbSearchTerm, ttfbSortOrder, ttfbSortField]);

  if (loading) {
    return <div className="text-gray-600 font-bold">Loading logs...</div>;
  }

  if (!callLogs) {
    return <div className="text-gray-600">No logs available</div>;
  }

  return (
    <Accordion type="multiple" defaultValue={["ttfb", "events"]}>
      <AccordionItem value="ttfb">
        <AccordionTrigger>
          <div className="flex items-center justify-between flex-1">
            <span className="text-sm font-bold">
              Time to First Byte (TTFB) Metrics
            </span>
            <span className="ml-2 text-xs bg-gray-200 px-2 py-1 rounded-full">
              {ttfbSearchTerm
                ? `${filteredTtfb?.length}/${callLogs?.ttfb?.length}`
                : callLogs?.ttfb?.length}
            </span>
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <Input
                type="text"
                placeholder="Search TTFB entries..."
                value={ttfbSearchTerm}
                onChange={(e) => setTtfbSearchTerm(e.target.value)}
                className="w-64"
              />
              <div className="flex items-center gap-4">
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setTtfbSortField("ttfb");
                      setTtfbSortOrder(
                        ttfbSortOrder === "asc" ? "desc" : "asc"
                      );
                    }}
                    className={`flex items-center gap-1 ${
                      ttfbSortField === "ttfb" ? "bg-gray-100" : ""
                    }`}
                  >
                    Sort by TTFB{" "}
                    {ttfbSortField === "ttfb" &&
                      (ttfbSortOrder === "asc" ? "↑" : "↓")}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setTtfbSortField("timestamp");
                      setTtfbSortOrder(
                        ttfbSortOrder === "asc" ? "desc" : "asc"
                      );
                    }}
                    className={`flex items-center gap-1 ${
                      ttfbSortField === "timestamp" ? "bg-gray-100" : ""
                    }`}
                  >
                    Sort by Time{" "}
                    {ttfbSortField === "timestamp" &&
                      (ttfbSortOrder === "asc" ? "↑" : "↓")}
                  </Button>
                </div>
                <div className="flex items-center space-x-1">
                  <span className="text-xs text-gray-600">Response time:</span>
                  <span className="h-2 w-2 rounded-full bg-green-500 ml-2"></span>
                  <span className="text-xs text-gray-600">
                    Fast (&lt;300ms)
                  </span>
                  <span className="h-2 w-2 rounded-full bg-yellow-400 ml-2"></span>
                  <span className="text-xs text-gray-600">
                    Medium (&lt;500ms)
                  </span>
                  <span className="h-2 w-2 rounded-full bg-red-400 ml-2"></span>
                  <span className="text-xs text-gray-600">Slow (≥500ms)</span>
                </div>
              </div>
            </div>
            <ScrollArea className="h-[400px] w-full rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-1/4">Date/Time</TableHead>
                    <TableHead className="w-1/4">Service</TableHead>
                    <TableHead className="w-2/4">TTFB (ms)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTtfb.map((entry, index) => {
                    // Convert all values to milliseconds
                    const ttfbInMs = entry.ttfb * 1000;
                    // Find max TTFB for scaling bars
                    const maxTtfb = Math.max(
                      ...filteredTtfb.map((e) => e.ttfb * 1000)
                    );
                    // Calculate relative width percentage with a minimum of 5%
                    const barWidth = Math.max(5, (ttfbInMs / maxTtfb) * 100);

                    // Determine color based on response time
                    const getBarColor = () => {
                      if (ttfbInMs < 100) return "bg-green-500"; // Very fast: < 100ms
                      if (ttfbInMs < 300) return "bg-green-400"; // Fast: < 300ms
                      if (ttfbInMs < 500) return "bg-yellow-400"; // Medium: < 500ms
                      return "bg-red-400"; // Slow: >= 500ms
                    };

                    return (
                      <TableRow
                        key={index}
                        className={`hover:bg-gray-100 ${
                          index % 2 === 0 ? "bg-gray-50" : "bg-white"
                        }`}
                        title={`Response time: ${ttfbInMs.toFixed(2)}ms`}
                      >
                        <TableCell className="font-mono text-xs whitespace-nowrap">
                          {entry.date_time}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-medium ${
                              entry.service_name.includes("OpenAI")
                                ? "bg-green-100 text-green-800"
                                : entry.service_name.includes("ElevenLabs")
                                ? "bg-blue-100 text-blue-800"
                                : entry.service_name.includes("Deepgram")
                                ? "bg-purple-100 text-purple-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {entry.service_name}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col w-full">
                            <div className="flex justify-between mb-1">
                              <span className="text-xs font-mono font-medium">
                                {ttfbInMs.toFixed(2)}ms
                              </span>
                            </div>
                            <div className="w-full h-1 bg-gray-100 rounded-full">
                              <div
                                className={`h-full rounded-full ${getBarColor()} transition-all duration-300`}
                                style={{ width: `${barWidth}%` }}
                              ></div>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ScrollArea>
          </div>
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="events">
        <AccordionTrigger>
          <div className="flex items-center justify-between flex-1">
            <span className="text-sm font-bold">Event Logs</span>
            <span className="ml-2 text-xs bg-gray-200 px-2 py-1 rounded-full">
              {eventSearchTerm
                ? `${filteredEvents?.length}/${callLogs?.events?.length}`
                : callLogs?.events?.length}
            </span>
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <div className="mb-4">
            <Input
              type="text"
              placeholder="Search events..."
              value={eventSearchTerm}
              onChange={(e) => setEventSearchTerm(e.target.value)}
              className="w-full"
            />
          </div>
          <ScrollArea className="h-[400px] max-w-[65vw] rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-1/4">Date/Time</TableHead>
                  <TableHead>Event</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEvents.map((log, index) => (
                  <TableRow
                    key={index}
                    className={`hover:bg-gray-100 ${
                      index % 2 === 0 ? "bg-gray-50" : "bg-white"
                    }`}
                  >
                    <TableCell className="font-mono text-xs whitespace-nowrap">
                      {log.date_time}
                    </TableCell>
                    <TableCell className="font-mono text-xs whitespace-pre-wrap break-words">
                      {log.event.includes(" - ") ? (
                        <>
                          <span className="text-gray-400 font-mono text-xs">
                            {log.event.split(" - ")[0]}
                          </span>
                          <span className="ml-2 pl-2 border-gray-200">
                            {log.event.split(" - ").slice(1).join(" - ")}
                          </span>
                        </>
                      ) : (
                        <span>{log.event}</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default CallLogs;
